import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DateRangeSelector = ({ dateRange, onDateRangeChange }) => {
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const presetRanges = [
    {
      label: 'Last 7 Days',
      value: 'week',
      icon: 'Calendar',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start?.setDate(start?.getDate() - 7);
        return { start, end };
      }
    },
    {
      label: 'Last 30 Days',
      value: 'month',
      icon: 'CalendarDays',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start?.setDate(start?.getDate() - 30);
        return { start, end };
      }
    },
    {
      label: 'Last 90 Days',
      value: 'quarter',
      icon: 'CalendarRange',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start?.setDate(start?.getDate() - 90);
        return { start, end };
      }
    }
  ];

  const handlePresetClick = (preset) => {
    const dates = preset?.getDates();
    onDateRangeChange(dates);
    setShowCustomRange(false);
  };

  const handleCustomRangeSubmit = (e) => {
    e?.preventDefault();
    if (customStart && customEnd) {
      onDateRangeChange({
        start: new Date(customStart),
        end: new Date(customEnd)
      });
      setShowCustomRange(false);
    }
  };

  const formatDisplayDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-therapeutic p-4 md:p-6">
      <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4 md:mb-6">
        Select Date Range
      </h3>
      <div className="mb-4 md:mb-6 p-3 md:p-4 bg-primary/10 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm md:text-base text-foreground font-medium">Current Range:</span>
          <span className="text-sm md:text-base text-primary font-semibold">
            {formatDisplayDate(dateRange?.start)} - {formatDisplayDate(dateRange?.end)}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-4">
        {presetRanges?.map((preset) => (
          <button
            key={preset?.value}
            onClick={() => handlePresetClick(preset)}
            className="flex flex-col items-center justify-center gap-2 p-4 md:p-5 rounded-lg border-2 border-border bg-background hover:border-primary hover:bg-primary/5 transition-smooth focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background active:scale-[0.97] min-h-[80px] md:min-h-[100px]"
          >
            <Icon name={preset?.icon} size={24} color="var(--color-primary)" strokeWidth={2} />
            <span className="text-sm md:text-base font-medium text-foreground">
              {preset?.label}
            </span>
          </button>
        ))}
      </div>
      <Button
        variant="outline"
        size="default"
        fullWidth
        iconName={showCustomRange ? 'ChevronUp' : 'ChevronDown'}
        iconPosition="right"
        onClick={() => setShowCustomRange(!showCustomRange)}
        className="min-h-[44px]"
      >
        Custom Date Range
      </Button>
      {showCustomRange && (
        <form
          onSubmit={handleCustomRangeSubmit}
          className="mt-4 p-4 md:p-6 bg-muted/50 rounded-lg space-y-4 animate-in fade-in slide-in-from-top-2 duration-200"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="start-date" className="block text-sm font-medium text-foreground mb-2">
                Start Date
              </label>
              <input
                id="start-date"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e?.target?.value)}
                max={customEnd || new Date()?.toISOString()?.split('T')?.[0]}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring transition-smooth"
                required
              />
            </div>

            <div>
              <label htmlFor="end-date" className="block text-sm font-medium text-foreground mb-2">
                End Date
              </label>
              <input
                id="end-date"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e?.target?.value)}
                min={customStart}
                max={new Date()?.toISOString()?.split('T')?.[0]}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring transition-smooth"
                required
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={() => setShowCustomRange(false)}
              className="flex-1 min-h-[44px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              size="default"
              iconName="Check"
              iconPosition="right"
              className="flex-1 min-h-[44px]"
            >
              Apply Range
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default DateRangeSelector;